import React, { useEffect, useRef, useState } from 'react';
import * as monaco from 'monaco-editor';
import { updateMonacoState } from '../../services/interview.service';

// Import and set up Monaco workers
import editorWorker from 'monaco-editor/esm/vs/editor/editor.worker?worker';
import jsonWorker from 'monaco-editor/esm/vs/language/json/json.worker?worker';
import cssWorker from 'monaco-editor/esm/vs/language/css/css.worker?worker';
import htmlWorker from 'monaco-editor/esm/vs/language/html/html.worker?worker';
import tsWorker from 'monaco-editor/esm/vs/language/typescript/ts.worker?worker';

if (typeof window !== 'undefined') {
  self.MonacoEnvironment = {
    getWorker: function (_, label) {
      if (label === 'json') {
        return new jsonWorker();
      }
      if (label === 'css' || label === 'scss' || label === 'less') {
        return new cssWorker();
      }
      if (label === 'html' || label === 'handlebars' || label === 'razor') {
        return new htmlWorker();
      }
      if (label === 'typescript' || label === 'javascript') {
        return new tsWorker();
      }
      return new editorWorker();
    }
  };
}

// Available programming languages
const SUPPORTED_LANGUAGES = [
  { value: 'typescript', label: 'TypeScript', icon: '🟦' },
  { value: 'javascript', label: 'JavaScript', icon: '🟨' },
  { value: 'python', label: 'Python', icon: '🐍' },
  { value: 'java', label: 'Java', icon: '☕' },
  { value: 'sql', label: 'SQL', icon: '🗃️' },
  { value: 'cpp', label: 'C++', icon: '⚡' },
  { value: 'csharp', label: 'C#', icon: '🔷' },
  { value: 'xml', label: 'XML', icon: '📄' },
  { value: 'php', label: 'PHP', icon: '🐘' },
  { value: 'razor', label: 'Razor', icon: '📄' },
  { value: 'batch', label: 'Batch', icon: '📄' },
  { value: 'powershell', label: 'Powershell', icon: '📄' },
  { value: 'ruby', label: 'Ruby', icon: '💎' },
  { value: 'sass', label: 'SASS', icon: '🎨' },
  { value: 'r', label: 'R', icon: '📊' },
  { value: 'objective-c', label: 'Objective-C', icon: '📱' },
  { value: 'shell', label: 'Shell', icon: '📄' },
] as const;

// Language mapping for Monaco Editor
const getMonacoLanguage = (language: string): string => {
  const langMap: Record<string, string> = {
    'typescript': 'typescript',
    'javascript': 'javascript',
    'python': 'python',
    'java': 'java',
    'sql': 'sql',
    'cpp': 'cpp',
    'c++': 'cpp',
    'csharp': 'csharp',
    'c#': 'csharp',
    'cs': 'csharp',
    'js': 'javascript',
    'ts': 'typescript',
    'py': 'python',
    'mysql': 'sql',
    'postgresql': 'sql',
    'postgres': 'sql',
    'xml': 'xml',
    'php': 'php',
    'razor': 'razor',
    'batch': 'batch',
    'powershell': 'powershell',
    'ruby': 'ruby',
    'sass': 'sass',
    'r': 'r',
    'objective-c': 'objective-c',
    'shell': 'shell',
  };
  return langMap[language.toLowerCase()] || 'javascript';
};

// Helper to get sample code
const getSampleCode = (lang: string): string => {
  switch (lang) {
    case 'python':
      return `# Write your Python code here\n\n`;
    case 'java':
      return `// Write your Java code here\n\npublic class Main {\n    public static void main(String[] args) {\n        \n    }\n}`;
    case 'sql':
      return `-- Write your SQL query here\n\nSELECT *\nFROM table_name;\n`;
    case 'cpp':
      return `// Write your C++ code here\n\n#include <iostream>\nusing namespace std;\n\nint main() {\n    \n    return 0;\n}`;
    case 'csharp':
      return `// Write your C# code here\n\nusing System;\n\nclass Program \n{\n    static void Main() \n    {\n        \n    }\n}`;
    case 'javascript':
      return `// Write your JavaScript code here\n\n`;
    case 'typescript':
      return `// Write your TypeScript code here\n\n`;
    default:
      return `// Write your code here\n\n`;
  }
};

interface MonacoIDEProps {
  questionIndex: number;
  questionKind: string;
  isReadOnly: boolean;
  initialCode?: string;
  language?: string;
  onCodeChange?: (code: string) => void;
  onLanguageChange?: (language: string) => void;
  className?: string;
  showLanguageSelector?: boolean;
  interviewId?: string;
}

export const MonacoIDE: React.FC<MonacoIDEProps> = ({
  questionIndex,
  questionKind,
  isReadOnly,
  initialCode = '',
  language: propLanguage = 'typescript',
  onCodeChange,
  onLanguageChange,
  className = '',
  showLanguageSelector = true,
  interviewId
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const monacoRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const onCodeChangeRef = useRef(onCodeChange);

  const [currentLanguage, setCurrentLanguage] = useState(() => getMonacoLanguage(propLanguage));
  const prevLanguageRef = useRef<string>(currentLanguage);

  // Keep the onCodeChange callback in a ref to avoid re-running effects
  useEffect(() => {
    onCodeChangeRef.current = onCodeChange;
  }, [onCodeChange]);
  
  // Sync currentLanguage with propLanguage from parent
  useEffect(() => {
    const newLang = getMonacoLanguage(propLanguage);
    if (newLang !== currentLanguage) {
      setCurrentLanguage(newLang);
    }
  }, [propLanguage]);

  // UI handler for dropdown change. Updates state instantly for a responsive UI.
  const handleLanguageChange = (newLangValue: string) => {
    prevLanguageRef.current = currentLanguage;
    const newLang = getMonacoLanguage(newLangValue);
    setCurrentLanguage(newLang);
    onLanguageChange?.(newLang);
  };
  
  // Effect to update the editor model when the language changes.
  useEffect(() => {
    const editor = monacoRef.current;
    if (!editor) return;

    const model = editor.getModel();
    if (model && model.getLanguageId() !== currentLanguage) {
      console.log(`[MonacoIDE] Updating model language from ${model.getLanguageId()} to ${currentLanguage}`);
      monaco.editor.setModelLanguage(model, currentLanguage);
      
      if (!isReadOnly) {
        const currentCode = editor.getValue();
        const oldSampleCode = getSampleCode(prevLanguageRef.current);
        
        if (currentCode.trim() === '' || currentCode === oldSampleCode) {
          const newSampleCode = getSampleCode(currentLanguage);
          editor.setValue(newSampleCode);
        }
      }
    }
  }, [currentLanguage, questionKind, isReadOnly]);

  // Effect to update editor content when initialCode changes from props.
  useEffect(() => {
    const editor = monacoRef.current;
    if (editor && initialCode !== undefined) {
      if (editor.getValue() !== initialCode) {
        console.log(`[MonacoIDE] Updating code from prop for question ${questionIndex}`);
        editor.setValue(initialCode);
      }
    }
  }, [initialCode, questionIndex]);

  // Main editor initialization effect.
  // Creates and disposes the editor instance. Runs only when the question or read-only status changes.
  useEffect(() => {
    if (!editorRef.current) return;

    console.log(`[MonacoIDE] Initializing editor for question ${questionIndex}`);
    
    const effectiveCode = initialCode || (!isReadOnly ? getSampleCode(currentLanguage) : '');

    const editor = monaco.editor.create(editorRef.current, {
      value: effectiveCode,
      language: currentLanguage,
      theme: 'vs',
      readOnly: isReadOnly,
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      lineNumbers: 'on',
      glyphMargin: false,
      folding: true,
      lineDecorationsWidth: 0,
      automaticLayout: true,
      wordWrap: 'on',
      fontSize: 14,
      tabSize: 2,
      insertSpaces: true,
      contextmenu: !interviewId, // Disable context menu only during interviews
      quickSuggestions: !isReadOnly,
      scrollbar: {
        vertical: 'visible', // Always show vertical scrollbar
        horizontal: 'auto',
        handleMouseWheel: true, // Let Monaco handle wheel events normally
        verticalScrollbarSize: 10,
      },
    });
    
    monacoRef.current = editor;

    // Smart scroll handling: Check boundaries BEFORE Monaco processes, then prevent page scroll if Monaco can scroll
    const editorDomNode = editor.getDomNode();
    let captureHandler: ((e: WheelEvent) => void) | null = null;
    let bubbleHandler: ((e: WheelEvent) => void) | null = null;
    let keydownHandler: ((e: KeyboardEvent) => void) | null = null;
    let pasteHandler: ((e: ClipboardEvent) => void) | null = null;
    let contextmenuHandler: ((e: MouseEvent) => void) | null = null;

    if (editorDomNode) {
      // Capture phase: Store scroll position before Monaco processes
      captureHandler = (e: WheelEvent) => {
        // Store scroll position before Monaco processes the event
        (e as any).__scrollTopBefore = editor.getScrollTop();
      };

      // Bubble phase: Check if Monaco scrolled, if not (at boundary), scroll parent container
      bubbleHandler = (e: WheelEvent) => {
        const scrollTopBefore = (e as any).__scrollTopBefore;
        const scrollTopAfter = editor.getScrollTop();
        const scrollHeight = editor.getScrollHeight();
        const viewportHeight = editor.getLayoutInfo().height;
        const maxScrollTop = Math.max(0, scrollHeight - viewportHeight);

        const scrollingUp = e.deltaY < 0;
        const scrollingDown = e.deltaY > 0;

        // Check if Monaco actually scrolled
        const monacoScrolled = Math.abs(scrollTopAfter - scrollTopBefore) > 0.5;

        // Check if Monaco is at a boundary
        const isAtTop = scrollTopAfter <= 0.5;
        const isAtBottom = scrollTopAfter >= maxScrollTop - 0.5;

        if (monacoScrolled) {
          // Monaco scrolled, prevent page from scrolling
          e.stopPropagation();
        } else if ((isAtTop && scrollingUp) || (isAtBottom && scrollingDown)) {

          // Find the closest scrollable parent
          let scrollableParent: HTMLElement | null = editorDomNode.parentElement;
          let depth = 0;
          while (scrollableParent && depth < 20) {
            const overflow = window.getComputedStyle(scrollableParent).overflowY;

            if (overflow === 'auto' || overflow === 'scroll') {
              scrollableParent.scrollBy({ top: e.deltaY, behavior: 'auto' });
              break;
            }
            scrollableParent = scrollableParent.parentElement;
            depth++;
          }

          // Also try window scroll as fallback
          if (!scrollableParent || depth >= 20) {
            window.scrollBy({ top: e.deltaY, behavior: 'auto' });
          }
        } else {
          // Edge case: Monaco didn't scroll but not at boundary - prevent page scroll anyway
          e.stopPropagation();
        }
      };

      // Disable copy, paste, and cut during interview (when editor is editable)
      if (!isReadOnly && interviewId) {
        keydownHandler = (e: KeyboardEvent) => {
          const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.platform);
          const ctrlKey = isMac ? e.metaKey : e.ctrlKey;

          // Prevent Ctrl+C (copy), Ctrl+V (paste), Ctrl+X (cut)
          if (ctrlKey && (e.key.toLowerCase() === 'c' || e.key.toLowerCase() === 'v' || e.key.toLowerCase() === 'x')) {
            e.preventDefault();
          }
        };

        pasteHandler = (e: ClipboardEvent) => {
          e.preventDefault();
        };

        contextmenuHandler = (e: MouseEvent) => {
          e.preventDefault();
        };

        editorDomNode.addEventListener('keydown', keydownHandler);
        editorDomNode.addEventListener('paste', pasteHandler);
        editorDomNode.addEventListener('contextmenu', contextmenuHandler);
      }

      // Add both handlers
      editorDomNode.addEventListener('wheel', captureHandler, { capture: true, passive: true });
      editorDomNode.addEventListener('wheel', bubbleHandler, { passive: false });
    }

    const contentChangeSubscription = editor.onDidChangeModelContent(() => {
      if (!isReadOnly && onCodeChangeRef.current) {
        onCodeChangeRef.current(editor.getValue());
      }
    });

    return () => {
      console.log(`[MonacoIDE] Disposing editor for question ${questionIndex}`);
      if (editorDomNode) {
        if (captureHandler && bubbleHandler) {
          editorDomNode.removeEventListener('wheel', captureHandler, { capture: true });
          editorDomNode.removeEventListener('wheel', bubbleHandler);
        }
        if (keydownHandler) {
          editorDomNode.removeEventListener('keydown', keydownHandler);
        }
        if (pasteHandler) {
          editorDomNode.removeEventListener('paste', pasteHandler);
        }
        if (contextmenuHandler) {
          editorDomNode.removeEventListener('contextmenu', contextmenuHandler);
        }
      }
      contentChangeSubscription.dispose();
      editor.dispose();
      monacoRef.current = null;
    };
  }, [questionIndex, isReadOnly, questionKind]); // Re-create editor only if these critical props change.

  // Effect to record Monaco state every 5 seconds
  useEffect(() => {
    // Only record state if we have an interview ID, editor exists, and it's not read-only
    if (!interviewId || isReadOnly || !monacoRef.current) {
      return;
    }

    const recordState = () => {
      const editor = monacoRef.current;
      if (!editor) return;

      const code = editor.getValue();
      const language = currentLanguage;
      
      // Only send state if there's actual code content
      if (code.trim()) {
        console.log(`[MonacoIDE] Recording state for interview ${interviewId}, question ${questionIndex}`);
        updateMonacoState(interviewId, code, language, questionIndex)
          .then(response => {
            if (response.error) {
              console.error(`[MonacoIDE] Failed to record state:`, response.error);
            }
          })
          .catch(error => {
            console.error(`[MonacoIDE] Error recording state:`, error);
          });
      }
    };

    // Record state immediately when editor becomes active
    recordState();

    // Set up interval to record state every 5 seconds
    const intervalId = setInterval(recordState, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [interviewId, isReadOnly, questionIndex, currentLanguage]); // Re-run when these change

  return (
    <div className={`monaco-ide-container bg-white border border-gray-300 rounded-lg shadow-sm ${className}`}>
      <div className="bg-gray-100 px-4 py-2 border-b border-gray-300 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700">
            {isReadOnly ? '📖 Code Review' : '✏️ Code Editor'}
          </span>
          {showLanguageSelector && !isReadOnly ? (
            <select
              value={currentLanguage}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="text-xs bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {SUPPORTED_LANGUAGES.map(lang => (
                <option key={lang.value} value={lang.value}>
                  {lang.icon} {lang.label}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-xs text-gray-500">
              {SUPPORTED_LANGUAGES.find(lang => lang.value === currentLanguage)?.icon} {SUPPORTED_LANGUAGES.find(lang => lang.value === currentLanguage)?.label || currentLanguage.charAt(0).toUpperCase() + currentLanguage.slice(1)}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {isReadOnly && (
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
              Read-only
            </span>
          )}
          {!isReadOnly && (
            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
              Editable
            </span>
          )}
          {!['typescript', 'javascript'].includes(currentLanguage) && (
            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded" title="Basic syntax highlighting only">
              Basic support
            </span>
          )}
        </div>
      </div>
      
      <div 
        ref={editorRef} 
        className="w-full"
        style={{ 
          height: '400px',
          minHeight: '400px',
          minWidth: '200px',
          position: 'relative',
          overflow: 'hidden' // Contain the editor within its bounds
        }}
      />
      
      {!isReadOnly && (
        <div className="bg-gray-50 px-4 py-2 border-t border-gray-300 text-xs text-gray-600">
          💡 Tip: Use Ctrl+Space for autocomplete.
          {!['typescript', 'javascript'].includes(currentLanguage) && (
            <div className="mt-1 text-yellow-600">
              ⚠️ Note: This language has basic syntax highlighting only. Full IntelliSense is available for TypeScript & JavaScript.
            </div>
          )}
        </div>
      )}
    </div>
  );
}; 
